// src/components/ContactModal.jsx
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { X, Mail, Phone } from "lucide-react";
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.66);
  display: grid;
  place-items: center;
  z-index: 1000;
  backdrop-filter: blur(6px);
`;

const Dialog = styled.div`
  width: 92%;
  max-width: 520px;
  background: ${(p) =>
    p.theme.colors.card ||
    `linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))`};
  color: ${(p) => p.theme.colors.text};
  border-radius: 14px;
  box-shadow: 0 28px 80px rgba(2, 6, 23, 0.6);
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 12px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.muted};
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    color: ${(p) => p.theme.colors.text};
  }
  &:focus {
    outline: 3px solid rgba(74, 0, 224, 0.12);
  }
`;

const Heading = styled.h3`
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 900;
`;

const Text = styled.p`
  margin: 0 0 18px 0;
  color: ${(p) => p.theme.colors.muted};
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.a`
  display: flex;
  gap: 12px;
  align-items: center;
  text-decoration: none;
  color: ${(p) => p.theme.colors.text};
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.03);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(2, 6, 23, 0.35);
  }

  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.colors.accent};
  }
`;

const Small = styled.div`
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.muted};
`;

export default function ContactModal({
  open,
  onClose,
  phone = "+918510040060",
  email = "Ksgm012020@gmail.com",
}) {
  const portalRoot = typeof document !== "undefined" ? document.body : null;
  const dialogRef = useRef(null);
  const lastActiveRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // store last focused element to restore focus when modal closes
    lastActiveRef.current = document.activeElement;
    // focus the dialog
    setTimeout(() => dialogRef.current?.focus?.(), 0);

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      // basic trap: keep tab inside dialog
      if (e.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll(
          'a,button,input,[tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden"; // prevent background scroll

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      // restore focus
      try {
        lastActiveRef.current?.focus?.();
      } catch {}
    };
  }, [open, onClose]);

  if (!open || !portalRoot) return null;

  return ReactDOM.createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Contact options"
      onMouseDown={(e) => {
        // click outside closes
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <Dialog ref={dialogRef} tabIndex={-1}>
        <CloseBtn aria-label="Close contact modal" onClick={() => onClose?.()}>
          <X />
        </CloseBtn>

        <Heading>Get in touch</Heading>
        <Text>Quick contact options — tap to call or email.</Text>

        <ContactList>
          <Row href={`tel:${phone}`}>
            <Phone size={20} />
            <div>
              <div style={{ fontWeight: 800 }}>{phone}</div>
              <Small>Call us</Small>
            </div>
          </Row>

          <Row href={`mailto:${email}`}>
            <Mail size={20} />
            <div>
              <div style={{ fontWeight: 800 }}>{email}</div>
              <Small>Email us</Small>
            </div>
          </Row>
        </ContactList>
      </Dialog>
    </Overlay>,
    portalRoot
  );
}
